import React, { useCallback, useEffect, useLayoutEffect, useState } from "react";

const cardWidth = 320;
const cardHeight = 196;

const GuideTour = ({ open, steps, onClose }) => {
  const [index, setIndex] = useState(0);
  const [spot, setSpot] = useState(null);

  useEffect(() => {
    if (open) setIndex(0);
  }, [open]);

  const measure = useCallback((shouldScroll) => {
    const step = steps[index];
    if (!step) {
      setSpot(null);
      return;
    }
    const target = [...document.querySelectorAll("[data-guide]")]
      .find((element) => element.getAttribute("data-guide") === step.path);
    if (!target) {
      setSpot(null);
      return;
    }
    if (shouldScroll) {
      target.scrollIntoView({ block: window.innerWidth < 1024 ? "center" : "nearest", inline: "nearest", behavior: "auto" });
    }
    const box = target.getBoundingClientRect();
    const rect = {
      top: Math.max(8, box.top - 4),
      left: Math.max(8, box.left - 4),
      width: box.width + 8,
      height: box.height + 8
    };
    const wide = window.innerWidth >= 1024;
    let top = rect.top;
    let left = rect.left + rect.width + 16;
    let width = cardWidth;
    if (!wide) {
      width = Math.min(cardWidth, window.innerWidth - 24);
      left = 12;
      top = window.innerHeight - cardHeight - 16;
    } else if (left + width > window.innerWidth - 12) {
      left = Math.max(12, window.innerWidth - width - 12);
      top = rect.top + rect.height + 12;
    }
    if (top + cardHeight > window.innerHeight - 12) {
      top = Math.max(12, window.innerHeight - cardHeight - 12);
    }
    setSpot({ rect, top, left, width });
  }, [index, steps]);

  useLayoutEffect(() => {
    if (!open) return;
    measure(true);
  }, [open, measure]);

  useEffect(() => {
    if (!open) return undefined;
    const nav = document.querySelector("[data-guide-nav]");
    const onMove = () => measure(false);
    window.addEventListener("resize", onMove);
    window.addEventListener("scroll", onMove, true);
    nav?.addEventListener("scroll", onMove);
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("resize", onMove);
      window.removeEventListener("scroll", onMove, true);
      nav?.removeEventListener("scroll", onMove);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, measure, onClose]);

  if (!open || !steps.length) return null;

  const step = steps[index];
  const last = index === steps.length - 1;
  const cardStyle = spot
    ? { top: spot.top, left: spot.left, width: spot.width }
    : { top: "50%", left: "50%", width: Math.min(cardWidth, window.innerWidth - 24), transform: "translate(-50%, -50%)" };

  return (
    <div className="fixed inset-0 z-[80]" role="dialog" aria-modal="true" aria-labelledby="guide-title">
      {spot && (
        <div
          className="absolute rounded-2xl ring-2 ring-white pointer-events-none"
          style={{
            top: spot.rect.top,
            left: spot.rect.left,
            width: spot.rect.width,
            height: spot.rect.height,
            boxShadow: "0 0 0 9999px rgba(15, 23, 42, 0.55)"
          }}
        />
      )}
      {!spot && <div className="absolute inset-0 bg-slate-900/55" />}
      <div
        className="absolute rounded-2xl border border-teal-100 bg-white p-4 shadow-xl"
        style={cardStyle}
      >
        <p className="text-xs font-semibold text-teal-700">{index + 1} of {steps.length}</p>
        <h2 id="guide-title" className="mt-1 text-base font-semibold text-gray-900">{step.label}</h2>
        <p className="mt-1 text-sm text-gray-600">{step.guide}</p>
        <div className="mt-4 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 text-sm font-semibold text-gray-600 hover:text-gray-900"
          >
            Skip
          </button>
          <button
            type="button"
            onClick={() => {
              if (last) onClose();
              else setIndex((current) => current + 1);
            }}
            className="px-4 py-2 rounded-lg bg-teal-600 text-sm font-semibold text-white hover:bg-teal-700"
          >
            {last ? "Done" : "Next"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GuideTour;
